import { Component, ErrorInfo, ReactNode } from "react";
import { t as rawT, getStoredLanguage } from "@/lib/i18n";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** Maximum number of auto-retries before giving up (default: 3) */
  maxRetries?: number;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
  isRetrying: boolean;
  showDetails: boolean;
  isChunkLoadError: boolean;
}

const IS_DEV = import.meta.env.DEV;

const BACKOFF_BASE_MS = 1000;

class ErrorBoundary extends Component<Props, State> {
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      isRetrying: false,
      showDetails: true,
      isChunkLoadError: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // If the error is a Vite chunk loading error (happens after new deployments)
    // we force a hard reload of the page so the browser gets the new index.html
    const msg = error.message || "";
    const isChunkError =
      error.name === "TypeError" &&
      (msg.includes("Failed to fetch dynamically imported module") ||
        msg.includes("Importing a module script failed") ||
        msg.includes("is not a valid JavaScript MIME type") ||
        msg.includes("expected a JavaScript module script"));
    if (isChunkError) {
      window.location.reload();
      return { hasError: true, error, isChunkLoadError: true };
    }
    return { hasError: true, error, isChunkLoadError: false };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    console.error("[ErrorBoundary]", error, errorInfo);

    // Telegram'a gönder
    try {
      if (!this.state.isChunkLoadError) {
        fetch("https://dtrctflgqpgivvsprwxe.supabase.co/functions/v1/report-error", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            url: window.location.href,
            error: error.message || error.toString(),
            stack: errorInfo.componentStack || error.stack || "",
            userAgent: navigator.userAgent
          })
        }).catch(e => console.error("Error reporting failed:", e));
      }
    } catch (e) {
      console.error("Error reporting exception:", e);
    }
    
    // Production'da direkt ana sayfaya at
    if (!IS_DEV) {
      const isHomePage = window.location.pathname === "/";
      const lastErr = sessionStorage.getItem("b_err_ts");
      const now = Date.now();
      
      // Ana sayfada çöküyorsa sonsuz döngüye girmesin diye 5sn bekle
      if (isHomePage && lastErr && (now - parseInt(lastErr)) < 5000) {
        return;
      }
      
      sessionStorage.setItem("b_err_ts", now.toString());
      
      if (this.state.isChunkLoadError) {
        window.location.reload();
      } else {
        window.location.href = "/";
      }
    }
  }

  componentWillUnmount() {
    if (this.retryTimer) clearTimeout(this.retryTimer);
  }

  private handleRetry = () => {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isRetrying: false,
    });
  };

  private handleAutoRetry = () => {
    const maxRetries = this.props.maxRetries ?? 3;
    const { retryCount } = this.state;
    if (retryCount >= maxRetries) return;

    const delay = BACKOFF_BASE_MS * Math.pow(2, retryCount);
    this.setState({ isRetrying: true });

    this.retryTimer = setTimeout(() => {
      this.setState((prev) => ({
        hasError: false,
        error: null,
        errorInfo: null,
        isRetrying: false,
        retryCount: prev.retryCount + 1,
      }));
    }, delay);
  };

  private toggleDetails = () => {
    this.setState((prev) => ({ showDetails: !prev.showDetails }));
  };

  /** Helper to call t() with the current stored language */
  private t = (key: string) => rawT(key, getStoredLanguage());

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (!IS_DEV) {
      return null;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    const { error, errorInfo, retryCount, isRetrying, showDetails } = this.state;
    const maxRetries = this.props.maxRetries ?? 3;
    const canAutoRetry = retryCount < maxRetries;

    return (
      <div style={styles.container}>
        <div style={styles.card}>
          {/* Gradient accent bar */}
          <div style={styles.accentBar} />

          {/* Error icon */}
          <div style={styles.iconWrapper}>
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={styles.icon}
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>

          <h2 style={styles.title}>{this.t("error.title")}</h2>
          <p style={styles.subtitle}>
            {this.t("error.subtitle")}
          </p>

          {/* Action buttons */}
          <div style={styles.actions}>
            <button
              onClick={this.handleRetry}
              style={styles.primaryButton}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = "0.9";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = "1";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              {this.t("error.retry")}
            </button>

            <a
              href="/"
              style={styles.secondaryButton}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "hsl(var(--muted))";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              {this.t("error.home")}
            </a>
          </div>

          {/* Auto-retry with backoff */}
          {canAutoRetry && (
            <div style={styles.autoRetrySection}>
              {isRetrying ? (
                <p style={styles.autoRetryText}>
                  <span style={styles.spinner} />
                  Otomatik yeniden deneniyor… (Deneme {retryCount + 1}/{maxRetries})
                </p>
              ) : (
                <button
                  onClick={this.handleAutoRetry}
                  style={styles.linkButton}
                >
                  Otomatik yeniden dene ({maxRetries - retryCount} hak kaldı)
                </button>
              )}
            </div>
          )}

          {/* Always show collapsible error details */}
          {error && (
            <div style={styles.detailsSection}>
              <button
                onClick={this.toggleDetails}
                style={styles.detailsToggle}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{
                    transform: showDetails ? "rotate(90deg)" : "rotate(0deg)",
                    transition: "transform 0.2s ease",
                    marginRight: "6px",
                    flexShrink: 0,
                  }}
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
                Hata Detayları (Geliştirici Modu)
              </button>

              {showDetails && (
                <div style={styles.detailsContent}>
                  <p style={styles.errorName}>
                    {error.name}: {error.message}
                  </p>
                  {error.stack && (
                    <pre style={styles.stackTrace}>{error.stack}</pre>
                  )}
                  {errorInfo?.componentStack && (
                    <>
                      <p style={{ ...styles.errorName, marginTop: "12px" }}>
                        Bileşen Yığını:
                      </p>
                      <pre style={styles.stackTrace}>
                        {errorInfo.componentStack}
                      </pre>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Inline keyframe animation */}
        <style>{`
          @keyframes eb-fade-in {
            from { opacity: 0; transform: translateY(16px) scale(0.97); }
            to   { opacity: 1; transform: translateY(0) scale(1); }
          }
          @keyframes eb-spin {
            to { transform: rotate(360deg); }
          }
          @keyframes eb-gradient {
            0%   { background-position: 0% 50%; }
            50%  { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}</style>
      </div>
    );
  }
}

/* ---------- Inline styles using CSS custom properties (design tokens) ---------- */

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    padding: "24px",
    backgroundColor: "hsl(var(--background))",
  },
  card: {
    position: "relative",
    width: "100%",
    maxWidth: "480px",
    backgroundColor: "hsl(var(--card))",
    borderRadius: "16px",
    border: "1px solid hsl(var(--border))",
    padding: "40px 32px 32px",
    textAlign: "center",
    overflow: "hidden",
    boxShadow: "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
    animation: "eb-fade-in 0.45s cubic-bezier(0.16,1,0.3,1) both",
  },
  accentBar: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: "linear-gradient(90deg, hsl(var(--primary)), hsl(var(--primary)/0.6), hsl(var(--primary)))",
    backgroundSize: "200% 200%",
    animation: "eb-gradient 3s ease infinite",
  },
  iconWrapper: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "80px",
    height: "80px",
    borderRadius: "50%",
    backgroundColor: "hsl(var(--destructive)/0.1)",
    marginBottom: "20px",
  },
  icon: {
    color: "hsl(var(--destructive))",
  },
  title: {
    fontSize: "20px",
    fontWeight: 700,
    color: "hsl(var(--foreground))",
    margin: "0 0 8px",
    lineHeight: 1.3,
  },
  subtitle: {
    fontSize: "14px",
    lineHeight: 1.6,
    color: "hsl(var(--muted-foreground))",
    margin: "0 0 24px",
  },
  actions: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    alignItems: "center",
  },
  primaryButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: "40px",
    padding: "0 24px",
    borderRadius: "8px",
    border: "none",
    backgroundColor: "hsl(var(--primary))",
    color: "hsl(var(--primary-foreground))",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.15s ease",
    width: "100%",
    maxWidth: "240px",
  },
  secondaryButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: "40px",
    padding: "0 24px",
    borderRadius: "8px",
    border: "1px solid hsl(var(--border))",
    backgroundColor: "transparent",
    color: "hsl(var(--foreground))",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.15s ease",
    textDecoration: "none",
    width: "100%",
    maxWidth: "240px",
  },
  autoRetrySection: {
    marginTop: "20px",
    paddingTop: "16px",
    borderTop: "1px solid hsl(var(--border))",
  },
  autoRetryText: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "13px",
    color: "hsl(var(--muted-foreground))",
    margin: 0,
  },
  spinner: {
    display: "inline-block",
    width: "14px",
    height: "14px",
    border: "2px solid hsl(var(--border))",
    borderTopColor: "hsl(var(--primary))",
    borderRadius: "50%",
    animation: "eb-spin 0.6s linear infinite",
  },
  linkButton: {
    background: "none",
    border: "none",
    color: "hsl(var(--primary))",
    fontSize: "13px",
    cursor: "pointer",
    textDecoration: "underline",
    textUnderlineOffset: "3px",
    padding: 0,
  },
  detailsSection: {
    marginTop: "20px",
    textAlign: "left",
  },
  detailsToggle: {
    display: "inline-flex",
    alignItems: "center",
    background: "none",
    border: "none",
    color: "hsl(var(--muted-foreground))",
    fontSize: "12px",
    fontWeight: 500,
    cursor: "pointer",
    padding: "4px 0",
  },
  detailsContent: {
    marginTop: "8px",
    padding: "12px",
    borderRadius: "8px",
    backgroundColor: "hsl(var(--muted))",
    border: "1px solid hsl(var(--border))",
  },
  errorName: {
    fontSize: "12px",
    fontWeight: 600,
    color: "hsl(var(--destructive))",
    margin: "0 0 8px",
    wordBreak: "break-word",
  },
  stackTrace: {
    fontSize: "11px",
    lineHeight: 1.5,
    color: "hsl(var(--muted-foreground))",
    margin: 0,
    whiteSpace: "pre-wrap",
    wordBreak: "break-all",
    maxHeight: "200px",
    overflowY: "auto",
    fontFamily: "ui-monospace, 'Cascadia Code', 'Fira Code', monospace",
  },
};

export default ErrorBoundary;
